import * as vp from 'vopay';
import { Box, Select, DateRangePicker, AVAILABLE_TIMEZONES } from 'vopay';
import { FunctionComponent, useState } from 'react';
import styles from './FileUploadHistory.module.scss';
import { Button } from 'UI';
import { useTranslation } from 'react-i18next';
import { FileConversionFilterParams, FileConversionSearchParams } from './types';

type SearchFormProps = {
  conversionStatus: FileConversionSearchParams['conversionStatus'];
  paymentRail: FileConversionSearchParams['paymentRail'];
  service: FileConversionSearchParams['service'];
  timeZone: string;
  initialTimeRange: [vp.Timestamp, vp.Timestamp];
  onSearch: (searchParams: FileConversionFilterParams) => void;
};

type FC = FunctionComponent<SearchFormProps>;

const FileConversionTableFilters: FC = ({
  service,
  paymentRail,
  conversionStatus,
  timeZone,
  initialTimeRange,
  onSearch: search,
}: SearchFormProps) => {
  const [currentService, setCurrentService] = useState<string>(service);
  const [currentPaymentRail, setCurrentPaymentRail] = useState<string>(paymentRail);
  const [currentConversionStatus, setCurrentConversionStatus] = useState<string>(conversionStatus);
  const [currentTimeZone, setCurrentTimeZone] = useState<string>(timeZone);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp]>(initialTimeRange);
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.History.FileConversionHistoryForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));
  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  const transferTypeOptions = [
    { value: 'all', label: tGlobal('ServiceOptions.all') },
    { value: 'Credit', label: tGlobal('ServiceOptions.credit') },
    { value: 'Debit', label: tGlobal('ServiceOptions.debit') },
  ];

  const paymentRailOptions = [
    { value: 'all', label: tGlobal('PaymentRail.all') },
    { value: 'EFT', label: tGlobal('PaymentRail.EFT') },
    { value: 'ACH', label: tGlobal('PaymentRail.ACH') },
    { value: 'VopayInstant', label: tGlobal('PaymentRail.VopayInstant') },
    { value: 'RTP', label: tGlobal('PaymentRail.RTP') },
    { value: 'WIRE', label: tGlobal('PaymentRail.WIRE') },
  ];

  const statusOptions = [
    { value: 'all', label: t('Status.all') },
    { value: 'Completed', label: t('Status.completed') },
    { value: 'Failed', label: t('Status.failed') },
    { value: 'Processing', label: t('Status.processing') },
  ];

  return (
    <Box flx="row" gap="sm" wrap="wrap">
      <div className={styles.datePickerContainer}>
        <DateRangePicker
          onChange={(range) => {
            setTimeRange(range);
          }}
          initialTime={initialTimeRange}
          triggerVariant="outlineShadow"
        />
      </div>

      <Box width="200px">
        <Select
          version="2"
          placeholder={t('transferTypePlaceholder')}
          options={transferTypeOptions}
          name="service"
          value={currentService}
          sort={false}
          onChange={(e) => {
            setCurrentService(e.target.value);
          }}
        />
      </Box>
      <Box width="200px">
        <Select
          version="2"
          placeholder={t('paymentRailPlaceholder')}
          options={paymentRailOptions}
          name="paymentRail"
          value={currentPaymentRail}
          sort={false}
          onChange={(e) => {
            setCurrentPaymentRail(e.target.value);
          }}
        />
      </Box>
      <Box width="200px">
        <Select
          version="2"
          placeholder={t('statusPlaceholder')}
          options={statusOptions}
          name="conversionStatus"
          sort={false}
          value={currentConversionStatus}
          onChange={(e) => {
            setCurrentConversionStatus(e.target.value);
          }}
        />
      </Box>
      <Box width="260px">
        <Select
          version="2"
          placeholder={t('timeZonePlaceholder')}
          options={timeZoneOptions}
          name="timeZone"
          value={currentTimeZone}
          onChange={(e) => {
            setCurrentTimeZone(e.target.value as vp.Timezone);
          }}
        />
      </Box>
      <Button
        id="search-file-conversion-history"
        size="large"
        colorScheme="primary"
        onClick={() => {
          search({
            service: currentService as FileConversionSearchParams['service'],
            paymentRail: currentPaymentRail as FileConversionSearchParams['paymentRail'],
            conversionStatus: currentConversionStatus as FileConversionSearchParams['conversionStatus'],
            timeZone: currentTimeZone as vp.Timezone,
            timeRange,
          });
        }}
      >
        {t('searchButton')}
      </Button>
    </Box>
  );
};

export default FileConversionTableFilters;
